import { useEffect, useRef } from "react"
import { Box, CircularProgress } from "@mui/material"
import { faComments } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { ChatMessage } from "./orderChatTypes"
import { OrderChatMessageBubble } from "./OrderChatMessageBubble"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { EmptyContent } from "../skeleton/EmptyContent"

const useStyles = makeStyles()(() => ({
    container: {
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    centeredContainer: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}))

type OrderChatMessageListProps = Readonly<{
    messages: ChatMessage[]
    isLoading: boolean
}>

export function OrderChatMessageList({ messages, isLoading }: OrderChatMessageListProps) {
    const { classes } = useStyles()
    const endRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages.length])

    if (isLoading) {
        return (
            <Box className={classes.centeredContainer}>
                <CircularProgress size={32} />
            </Box>
        )
    }

    if (messages.length === 0) {
        return (
            <Box className={classes.centeredContainer}>
                <LocalizedStrict id="order-chat-empty" attrs={{ title: true, description: true }}>
                    <EmptyContent
                        icon={faComments}
                        backgroundColor={tailwindPalette.sky[500]}
                        title="No messages yet"
                        description="Start the conversation!"
                    />
                </LocalizedStrict>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {messages.map((message) => (
                <OrderChatMessageBubble key={message.id} message={message} />
            ))}
            <div ref={endRef} />
        </Box>
    )
}
